// Floating dev-only panel that lets an engineer flip their effective
// CSP Licences identity (UPN + roles) without re-signing into AAD.
// Renders ONLY in development builds; in any production build this
// component returns null and the module dead-code-eliminates from the
// final bundle via the NODE_ENV gate.

import React, { useState } from 'react'
import {
  readDevOverride,
  writeDevOverride,
  clearDevOverride,
  useDevOverrideState,
} from './devImpersonation'

const isDev = process.env.NODE_ENV === 'development'

const ROLE_PRESETS = [
  { label: 'Real account', roles: null },
  { label: 'Sales', roles: ['Sales'] },
  { label: 'ITManager', roles: ['ITManager'] },
  { label: 'SSE', roles: ['SSE'] },
  { label: 'Admin', roles: ['Admin'] },
  { label: 'Sales + Admin', roles: ['Sales', 'Admin'] },
]

const containerStyle = {
  position: 'fixed',
  bottom: 12,
  right: 12,
  zIndex: 2000,
  fontSize: 12,
  fontFamily: 'monospace',
}

const panelStyle = (active) => ({
  background: active ? '#fff3cd' : '#f6f8fa',
  border: `2px solid ${active ? '#fd7e14' : '#d0d7de'}`,
  borderRadius: 6,
  padding: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  maxWidth: 320,
})

const badgeStyle = (active) => ({
  background: active ? '#fd7e14' : '#6c757d',
  color: '#fff',
  borderRadius: 12,
  padding: '4px 10px',
  fontWeight: 'bold',
  cursor: 'pointer',
  userSelect: 'none',
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
})

const CSPLicenseDevPanel = () => {
  if (!isDev) return null

  const override = useDevOverrideState()
  const [expanded, setExpanded] = useState(override.active)
  const [draftUpn, setDraftUpn] = useState(override.upn || '')
  const [draftRoles, setDraftRoles] = useState((override.roles || []).join(','))

  // Re-sync drafts when override changes externally (e.g. another tab
  // cleared it). Keeps the panel's text fields honest.
  React.useEffect(() => {
    setDraftUpn(override.upn || '')
    setDraftRoles((override.roles || []).join(','))
  }, [override.upn, override.roles && override.roles.join(',')])

  if (!expanded) {
    return (
      <div style={containerStyle}>
        <div style={badgeStyle(override.active)} onClick={() => setExpanded(true)}>
          {override.active ? '⚠ DEV IMPERSONATION ACTIVE' : 'Dev panel'}
        </div>
      </div>
    )
  }

  const applyPreset = (roles) => {
    writeDevOverride({ upn: draftUpn || null, roles })
  }

  const applyDraft = () => {
    const roles = draftRoles
      ? draftRoles.split(',').map((r) => r.trim()).filter(Boolean)
      : null
    writeDevOverride({ upn: draftUpn || null, roles })
  }

  return (
    <div style={containerStyle}>
      <div style={panelStyle(override.active)}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ flexGrow: 1 }}>
            {override.active ? '⚠ DEV IMPERSONATION' : 'Dev impersonation'}
          </strong>
          <span
            onClick={() => setExpanded(false)}
            style={{ cursor: 'pointer', padding: '0 4px' }}
            title="Collapse"
          >
            ×
          </span>
        </div>

        <div style={{ marginBottom: 6, color: '#666' }}>
          Scoped to 365 Licences endpoints only. Real AAD identity is
          used everywhere else (Graph, CW, audit).
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 2 }}>Quick roles</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {ROLE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => applyPreset(preset.roles)}
                style={{
                  fontSize: 11,
                  padding: '2px 6px',
                  border: '1px solid #888',
                  borderRadius: 3,
                  background: arraysEqual(preset.roles, override.roles) ? '#fd7e14' : '#fff',
                  color: arraysEqual(preset.roles, override.roles) ? '#fff' : '#000',
                  cursor: 'pointer',
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 2 }}>Override UPN</div>
          <input
            type="text"
            value={draftUpn}
            onChange={(e) => setDraftUpn(e.target.value)}
            placeholder="leave blank to keep real UPN"
            style={{ width: '100%', fontSize: 11, padding: 2, fontFamily: 'monospace' }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 2 }}>Override roles (comma-separated)</div>
          <input
            type="text"
            value={draftRoles}
            onChange={(e) => setDraftRoles(e.target.value)}
            placeholder="e.g. Sales,Admin"
            style={{ width: '100%', fontSize: 11, padding: 2, fontFamily: 'monospace' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={applyDraft}
            style={{
              flexGrow: 1,
              padding: '4px 8px',
              background: '#0d6efd',
              color: '#fff',
              border: 'none',
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            Apply
          </button>
          <button
            onClick={clearDevOverride}
            style={{
              padding: '4px 8px',
              background: '#fff',
              border: '1px solid #888',
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        </div>

        {override.active && (
          <div style={{ marginTop: 8, padding: 6, background: '#fff', border: '1px dashed #fd7e14', borderRadius: 3 }}>
            <div><strong>Effective UPN:</strong> {override.upn || '(real)'}</div>
            <div><strong>Effective roles:</strong> {override.roles ? override.roles.join(', ') : '(real)'}</div>
          </div>
        )}
      </div>
    </div>
  )
}

const arraysEqual = (a, b) => {
  if (a === null && b === null) return true
  if (!a || !b) return false
  if (a.length !== b.length) return false
  const aSorted = [...a].sort()
  const bSorted = [...b].sort()
  return aSorted.every((v, i) => v === bSorted[i])
}

export default CSPLicenseDevPanel
